import {
  SeniorJobPositionReadable,
  seniorPositionsCreateMutation,
  seniorPositionsDestroyMutation,
  seniorPositionsListOptions,
  seniorPositionsListQueryKey,
  seniorPositionsPartialUpdateMutation,
  seniorPositionsRetrieveOptions
} from '@crm/api'
import { ConfirmModal } from '@crm/components'
import {
  FormModal,
  FormNomenclaturesSeniorPositions
} from '@crm/features/nomenclatures/components'
import { useAppNavigate } from '@crm/hooks/useAppNavigate'
import { useTableSorter } from '@crm/hooks/useTableSorter'
import { paths } from '@crm/routes'
import { defaultFilters } from '@crm/utils'
import { DayLabel, TableActions, TableContainer } from '@shared/components'
import {
  useInvalidatePredicateQueries,
  useQueryParams,
  useResetPagination
} from '@shared/hooks'
import { useLocales } from '@shared/locales'
import { showApiErrors } from '@shared/utils'
import { useMutation, useQuery } from '@tanstack/react-query'
import { App, Button, MenuProps, TableColumnsType } from 'antd'
import { Add, Edit, People, Trash } from 'iconsax-react'
import { useCallback, useMemo, useState } from 'react'

const formName = 'senior-positions'

export const NomenclaturesSeniorPositions = () => {
  const { t } = useLocales()
  const params = useQueryParams()
  const sorter = useTableSorter()
  const { notification } = App.useApp()
  const { invalidateQueries } = useInvalidatePredicateQueries()
  const { resetPagination } = useResetPagination()

  const [editItem, setEditItem] = useState<number>()
  const [deleteItem, setDeleteItem] = useState<number>()
  const [isCreate, setIsCreate] = useState(false)
  const navigate = useAppNavigate()

  const { data: seniorPositions, isLoading } = useQuery(
    seniorPositionsListOptions({ query: { ...defaultFilters, ...params } })
  )

  const getActionColumnItem: (id: number) => MenuProps['items'] = useCallback(
    (id) => [
      {
        label: t('nomenclature.edit'),
        key: 'edit',
        icon: <Edit size={20} />,
        onClick: () => setEditItem(id)
      },
      {
        label: t('nomenclature.edit_positions'),
        key: 'edit_positions',
        icon: <People size={20} />,
        onClick: () => navigate(`/nomenclatures/general/senior-positions/${id}`)
      },
      { type: 'divider' },
      {
        label: t('nomenclature.delete'),
        key: 'delete',
        icon: <Trash size={20} />,
        danger: true,
        onClick: () => setDeleteItem(id)
      }
    ],
    [navigate, t]
  )

  const columns: TableColumnsType<SeniorJobPositionReadable> = useMemo(
    () => [
      {
        title: t('nomenclature.name'),
        dataIndex: 'title',
        key: 'title',
        sorter: true
      },
      {
        title: t('filter.created_at'),
        dataIndex: 'created_at',
        key: 'created_at',
        sorter: true,
        render: (created_at) => <DayLabel date={created_at} />
      },
      {
        title: t('lead.modified_at'),
        dataIndex: 'modified_at',
        key: 'modified_at',
        sorter: true,
        render: (modified_at) => <DayLabel date={modified_at} />
      },
      {
        width: 50,
        fixed: 'right',
        key: 'actions',
        render: ({ id }) => <TableActions options={getActionColumnItem(id)} />
      }
    ],
    [t, getActionColumnItem]
  )

  const deleteSeniorPositionMutation = useMutation({
    ...seniorPositionsDestroyMutation(),
    onError: (e) => showApiErrors(e),
    onSuccess: () => {
      invalidateQueries([seniorPositionsListQueryKey()])
      resetPagination()
      onCloseModal()
      notification.success({
        message: t('success.success'),
        description: t('success.success_data_delete')
      })
    }
  })

  const onCloseModal = () => {
    setDeleteItem(undefined)
    setEditItem(undefined)
    setIsCreate(false)
  }

  const onDelete = () => {
    if (deleteItem)
      deleteSeniorPositionMutation.mutate({ path: { id: deleteItem } })
  }

  return (
    <>
      <TableContainer
        data={seniorPositions}
        columns={columns}
        loading={isLoading}
        rightChildren={
          <Button
            type="primary"
            onClick={() => setIsCreate(true)}
            icon={<Add size={20} />}
          >
            {t('nomenclature.add_senior_position')}
          </Button>
        }
        onChange={(_, __, result) => sorter(result)}
        onRow={(data) => ({
          onClick: () =>
            navigate(
              paths.admin.nomenclatures.general.seniorPosition(String(data.id))
            )
        })}
        rowClassName="pointer"
        bordered
      />

      <FormModal
        id={editItem}
        formName={formName}
        open={!!(editItem || isCreate)}
        getItem={seniorPositionsRetrieveOptions}
        onCreate={seniorPositionsCreateMutation}
        onUpdate={seniorPositionsPartialUpdateMutation}
        onCloseModal={onCloseModal}
      >
        <FormNomenclaturesSeniorPositions />
      </FormModal>

      <ConfirmModal
        open={!!deleteItem}
        onSuccess={onDelete}
        onCancel={onCloseModal}
        confirmLoading={deleteSeniorPositionMutation.isPending}
      />
    </>
  )
}
